import type { FC, ReactNode } from 'react'
import type { SiteImage } from '@/types/content'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface PhotoProps {
    image: SiteImage
    className?: string
    imageClassName?: string
    sizes?: string
    priority?: boolean
    showLabel?: boolean
    zoom?: boolean
    reveal?: boolean
}

export const labelFor = (image: SiteImage): string => {
    if (image.label === 'job') return 'ACM job photo'
    if (image.label === 'illustration') return 'Illustration'

    return image.caption ?? 'Local photo'
}

const Photo: FC<PhotoProps> = ({
    image,
    className,
    imageClassName,
    sizes = '(min-width: 1024px) 50vw, 100vw',
    priority = false,
    showLabel = true,
    zoom = false,
    reveal = false
}): ReactNode => {
    return (
        <figure
            className={cn('relative overflow-hidden rounded-2xl bg-muted', className)}
            {...(reveal ? { 'data-reveal': 'wipe' } : {})}
        >
            <Image
                src={image.src}
                alt={image.alt}
                fill
                sizes={sizes}
                priority={priority}
                className={cn('object-cover', zoom ? 'hero-zoom' : 'reveal-settle', imageClassName)}
                style={image.position ? { objectPosition: image.position } : undefined}
            />

            {showLabel && (
                <figcaption className='absolute bottom-3 left-3 max-w-[calc(100%-1.5rem)] rounded-md bg-navy-deep/85 px-2.5 py-1 text-sm font-semibold text-white'>
                    {labelFor(image)}
                    {image.credit && (
                        <span className='block text-xs font-normal text-white/85'>
                            Photo: {image.credit}
                        </span>
                    )}
                </figcaption>
            )}
        </figure>
    )
}

export default Photo
